'use strict';

/**
 * Pict-Provider-ProfileBlockAPI
 * -----------------------------
 * Browser-side client for ProfileBlock -- the stackable content blocks down a user's profile page,
 * below their personal moodboard. Each block has a BlockType (text | image | embed) and a Content
 * JSON payload shaped for that type, and an integer SortOrder that orders the page.
 *
 * Extends the generic EntityAPI (list/create/update/remove) with: listForUser (the owner's blocks,
 * ordered), parseContent (Content is stored as a JSON string and parsed on read), and reorder (write
 * each block's new position). Tenancy (IDCustomer) is enforced server-side; IDUser scopes a profile.
 */

const libPictProviderEntityAPI = require('./Pict-Provider-EntityAPI.js');
const libFrameworkClient = require('./FrameworkClient.js');

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'ProfileBlockAPI',
	Dependencies: [],
	RoutePrefix: '/1.0/',
	EntityName: 'ProfileBlock',
	PluralRoute: 'ProfileBlocks',
	IDField: 'IDProfileBlock'
};

class PictProviderProfileBlockAPI extends libPictProviderEntityAPI
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _DefaultProviderConfiguration, pOptions || {}), pServiceHash);
	}

	/**
	 * A user's blocks, ordered down the page by SortOrder.
	 * @param {number} pIDUser
	 * @returns {Promise<Array<object>>}
	 */
	listForUser(pIDUser)
	{
		let tmpFilter = 'FBV~IDUser~EQ~' + pIDUser;
		return libFrameworkClient.getJSON(this.fable, this._url(this.pluralRoute + '/FilteredTo/' + tmpFilter + '/0/250'))
			.then((pBody) =>
			{
				let tmpRows = Array.isArray(pBody) ? pBody : (pBody && pBody.Records) || [];
				tmpRows.sort((pA, pB) => (pA.SortOrder || 0) - (pB.SortOrder || 0));
				return tmpRows;
			})
			.catch(() => []);
	}

	/**
	 * Parse a block row's Content JSON (stored as a string; tolerant of an object or empty value).
	 * @param {object} pRow
	 * @returns {object}
	 */
	parseContent(pRow)
	{
		if (!pRow || pRow.Content == null) { return {}; }
		if (typeof pRow.Content === 'object') { return pRow.Content; }
		try
		{
			let tmpParsed = JSON.parse(pRow.Content);
			return (tmpParsed && typeof tmpParsed === 'object') ? tmpParsed : {};
		}
		catch (pError)
		{
			return {};
		}
	}

	/**
	 * Create a block for a user. Content is stringified so it round-trips regardless of how meadow
	 * treats the JSON column.
	 * @param {number} pIDUser
	 * @param {string} pBlockType - 'text' | 'image' | 'embed'
	 * @param {object} pContent
	 * @param {object} pExtra - optional extra fields (Title, SortOrder)
	 */
	createBlock(pIDUser, pBlockType, pContent, pExtra)
	{
		let tmpRecord = Object.assign({ IDUser: pIDUser, BlockType: pBlockType, SortOrder: 0 }, pExtra || {});
		tmpRecord.Content = JSON.stringify(pContent || {});
		return this.create(tmpRecord);
	}

	/**
	 * Update a block's Content (and optional Title), stringifying Content.
	 * @param {number} pID
	 * @param {object} pContent
	 * @param {object} pExtra
	 */
	updateBlock(pID, pContent, pExtra)
	{
		let tmpRecord = Object.assign({}, pExtra || {});
		tmpRecord.Content = JSON.stringify(pContent || {});
		return this.update(pID, tmpRecord);
	}

	/**
	 * Persist the order of a block list: write each block whose position changed to its new index.
	 * @param {Array<object>} pOrderedBlocks - blocks in their new top-to-bottom order
	 * @returns {Promise}
	 */
	reorder(pOrderedBlocks)
	{
		let tmpWrites = [];
		for (let i = 0; i < pOrderedBlocks.length; i++)
		{
			let tmpBlock = pOrderedBlocks[i];
			if ((tmpBlock.SortOrder || 0) !== i)
			{
				tmpBlock.SortOrder = i;
				tmpWrites.push(this.update(tmpBlock[this.idField], { SortOrder: i }));
			}
		}
		return Promise.all(tmpWrites);
	}
}

module.exports = PictProviderProfileBlockAPI;
module.exports.default_configuration = _DefaultProviderConfiguration;
